import _ = require("underscore");
import Mediator = require("../mediator");
import Utils = require("../utils");
import Portal = require("portal-gis");
import CommandController = require("./base/command-controller");

const log = Utils.log;

class Control {

    static about = `
Base controller used to manage configuration, event delgation and ux rendering via overriding _addChild_.
    `;

    static options = {
        "event": {
            "about": "event to raise when _trigger_ed by various UX interactions depending on the implementation",
        },
    }
    private _options: Rhythm.GIS.Dictionary<string>;

    /** child controls */
    controllers: Rhythm.GIS.Dictionary<Control> = {};

    /** child commands, if order matters manage with option values because merge is not condusive to order */
    commandControllers: Rhythm.GIS.Dictionary<CommandController<Control>> = {};

    controls: Rhythm.GIS.Dictionary<Portal.ControlLeaf>;
    commands: Rhythm.GIS.Dictionary<Portal.CommandLeaf>;
    events: Rhythm.GIS.Dictionary<Portal.EventsLeaf>;

    constructor(public configuration: Portal.ControlLeaf, public mediator: Mediator, public args = {}) {
        log("creating controller: ", configuration.id);

        this.controls = {};
        this.commands = {};
        this.events = {};

        let options = _.defaults(this.options, {
            "css-name": configuration.id
        });

        _.defaults(options, {
            css: false //`app/css/${options["css-name"]}.css`
        });

        options["css"] && Utils.withCss(options["css"]);
    }

    destroy() {
        // nothing to do?
    }

    // copied from base-command
    get options() {
        if (!this._options) {
            this._options = Utils.asDictionary(this.configuration.Options.Values);
        }
        return this._options;
    }

    // copied from base-command
    trigger(event?: any, args?: any) {
        switch (arguments.length) {
            case 0:
                args = {};
                event = null;
                break;
            case 1:
                if (typeof event === "string") {
                    args = {};
                } else {
                    args = event;
                    event = null;
                }
                break;
        }

        // did I get it right?
        if (event) {
            if (typeof event !== "string") {
                throw "event name should be the first parameter";
            }
        }

        if (args) {
            if (typeof args !== "object") {
                throw "output args should be the second parameter";
            }
            if (args.in || args.out) {
                throw "trigger should not have in/out attributes";
            }
        }

        // ensure in/out pattern, in => command input, out => command output
        args = {
            in: this,
            out: args || {}
        };

        // inject this controller and view into command arguments
        _.defaults(args.out, {
            view: this["view"], // TODO: why are controller and view distinct?
            controller: this
        });

        let events = [];

        if (event) {
            events.push(event);
        } else {
            events.push(this.configuration.id);
            Utils.ifOption(this.options, "event", v => events = Utils.split(v));
        }

        events.forEach(event => {
            log(`controller ${this.configuration.id} triggering ${event}`);
            this.mediator.trigger(event, args);
        });
    }

    mergeControls(config: Portal.ControlsElement) {
        this.mediator.api.mergeControls(config, this);
    }

    mergeEvents(config: Portal.EventsElement) {
        this.mediator.api.mergeEvents(config, this);
    }

    mergeCommands(config: Portal.CommandsElement) {
        this.mediator.api.mergeCommands(config, this);
    }

    /** extends controller by registering commands, controls and events */
    register() {
        this.mediator.api.updateController(this);
    }

    registerControls(controls = this.configuration.Controls) {
        // allow app to instantiate and start view controller
        return this.mediator.api.registerControls(controls, this);
    }

    registerEvents(events: Portal.EventsElement) {
        // adds handlers to mediator
        return this.mediator.api.registerEvents(events, this);
    }

    registerCommands(commands: Portal.CommandsElement = this.configuration.Commands) {
        // instantiates these commands, executes 'startup' commands
        return this.mediator.api.registerCommands(commands, this);
    }

    public addChild(controller: Control) {
        throw "you must shadow the addChild method";
    }

    public show(args: {
        controller: Control;
        view: Backbone.View<Backbone.Model>;
        control: Portal.ControlLeaf;
    }) {
        this.addChild(args.controller);
    }

    public addButton(command: Portal.CommandLeaf, container?: JQuery) {
        throw "abstract";
    }

}

interface View extends Control {
    addButton(command: Portal.CommandLeaf, container?: JQuery): JQuery;
}

export = Control;